import * as Sentry from "@sentry/react";

import { isTranslationActive } from "~/lib/detectTranslation";
import { isDesktopApp } from "~/lib/utils/platform";

/**
 * Makes React resilient to DOM mutations from browser page-translation.
 *
 * Page-translators replace text nodes in place; when React's reconciler later
 * removes/moves a node translation already relocated, the DOM throws
 * "NotFoundError: ... removeChild ... not a child of this node" (React #11538).
 * We monkeypatch `removeChild`/`insertBefore` to no-op (instead of throw) when
 * the target isn't a child of the given parent; React recovers on the next
 * commit. Small per-call overhead, inherent to the fix per the React team.
 *
 * Copied from Dan Abramov's patch for react.dev:
 *   https://github.com/reactjs/react.dev/pull/1148
 *   https://github.com/facebook/react/issues/11538#issuecomment-417504600
 *
 * The no-op path also hides *genuine* reconciler bugs and non-translation DOM
 * mutators (e.g. Grammarly), so we surface those via Sentry — see `reportNoop`.
 */

type PatchedMethod = "removeChild" | "insertBefore";

// One captured event per session — no-ops can fire many times per commit.
let reported = false;

function reportNoop(method: PatchedMethod, node: Node, parent: Node): void {
	const message = `patchDOMForGoogleTranslate: no-op ${method} — target is not a child of the given parent`;

	if (typeof console !== "undefined") {
		console.error(message, node, parent);
	}

	// Cheap breadcrumb on every no-op, for context on any later captured error.
	Sentry.addBreadcrumb({ category: "dom-patch", level: "warning", message });

	// Capture only the *unexplained* no-ops, once per session: Electron (no
	// built-in translator, so a no-op means a Grammarly-style mutator or a real
	// reconciler bug) or a browser without translation active. Browser +
	// translation is the known cause (React #11538); breadcrumbs are enough there,
	// and skipping it keeps known-cause volume from burying the interesting events
	// in the same Sentry issue and preserves the once-per-session slot for them.
	const translationActive = isTranslationActive();
	if (reported || (translationActive && !isDesktopApp)) {
		return;
	}
	reported = true;

	Sentry.captureMessage(message, {
		level: "warning",
		tags: {
			// eslint-disable-next-line camelcase -- Sentry tag keys are snake_case by convention
			translation_active: translationActive,
			// eslint-disable-next-line camelcase -- Sentry tag keys are snake_case by convention
			is_electron: isDesktopApp,
		},
	});
}

/** Test-only: clear the per-session capture guard between cases. */
export function resetNoopReportingForTests(): void {
	reported = false;
}

let patched = false;

export function patchDOMForGoogleTranslate(): void {
	if (patched || typeof Node !== "function" || !Node.prototype) {
		return;
	}
	patched = true;

	const originalRemoveChild = Node.prototype.removeChild;
	Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
		if (child.parentNode !== this) {
			reportNoop("removeChild", child, this);
			return child;
		}
		return originalRemoveChild.call(this, child) as T;
	};

	const originalInsertBefore = Node.prototype.insertBefore;
	Node.prototype.insertBefore = function insertBefore<T extends Node>(
		this: Node,
		newNode: T,
		referenceNode: Node | null,
	): T {
		if (referenceNode && referenceNode.parentNode !== this) {
			reportNoop("insertBefore", referenceNode, this);
			return newNode;
		}
		return originalInsertBefore.call(this, newNode, referenceNode) as T;
	};
}
